import React from "react";

import CodeIcon from "@mui/icons-material/Code";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { Application, StackStatus } from "src/domains/application/types";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

interface VcsRepositorySectionProps {
  system: Application;
  onRetryProvisioning?: () => Promise<void>;
  retryProvisioningLoading?: boolean;
}

export function VcsRepositorySection({
  system,
  onRetryProvisioning,
  retryProvisioningLoading = false,
}: VcsRepositorySectionProps): React.ReactElement {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
        VCS Repository
      </Typography>
      <Box
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        {system.vcsRepoUrl ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CodeIcon fontSize="small" color="success" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                component="a"
                href={system.vcsRepoUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {system.vcsRepoUrl}
              </Typography>
            </Box>
            <Chip
              label="Connected"
              size="small"
              color="success"
              variant="outlined"
            />
          </Stack>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {system.status === StackStatus.PROVISIONING ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Repository provisioning in progress...
                </Typography>
              </Box>
            ) : system.status === StackStatus.PROVISIONING_FAILED ? (
              <Stack spacing={1} sx={{ width: "100%" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WarningAmberIcon fontSize="small" color="warning" />
                  <Typography variant="body2" color="warning.main">
                    VCS provisioning failed
                  </Typography>
                  <Chip
                    label="Failed"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Connect a GitHub organization first, then retry provisioning.
                </Typography>
                {onRetryProvisioning && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    startIcon={
                      retryProvisioningLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )
                    }
                    onClick={onRetryProvisioning}
                    disabled={retryProvisioningLoading}
                    sx={{ mt: 0.5, alignSelf: "flex-start" }}
                  >
                    Retry Provisioning
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <CodeIcon fontSize="small" color="disabled" />
                <Typography variant="body2" color="text.secondary">
                  No VCS repository configured
                </Typography>
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default VcsRepositorySection;
